'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useContext } from 'react';

import SegmentClick from '@flipster/features/src/misc/segment/segment-click';
import { Button } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Margins, marginsBunja } from '@flipster/socket/r230522/margin';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import Num, { countDecimalPlaces, numberFormatter } from '@flipster/utils/num';
import { StakeEvent } from '@flipster/utils/segment/events/stake';
import { OrderMarginType } from '@flipster/utils/types/base';

import { useWalletModal } from '@web/app/[locale]/(web)/_modal/WalletModal/hooks/useWalletModal';
import { useSymbolMarginType } from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/bunja/base';
import { maxPositionLimit } from '@web/constants/position';

import { PositionOrderButton, PositionOrderCard } from './position-order';
import { HedgingFormContext, hedgingFormBunja } from './state';

interface Props {
  onClose: () => void;
}

const HedgingForm = ({ onClose }: Props) => {
  const { stakingCurrency, stakedAmount } = useContext(HedgingFormContext);
  const { symbolInfoAtom, shortOpenPositionsAtom } = useBunja(hedgingFormBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const shortOpenPositions = useAtomValue(shortOpenPositionsAtom);

  if (!symbolInfo) return null;

  const { unitOrderQty } = symbolInfo;

  return (
    <>
      <Modal.Content css={{ textStyle: 'Text/L/Regular', color: 'content.default.level-3' }}>
        <p>Protect your staked amount from price drops with a short position.</p>
        <dl className={css({ mt: 16 })}>
          <div className={sectionDescriptionItem}>
            <dt>Total staked</dt>
            <dd>
              <span className={sectionPrice}>
                {numberFormatter(stakedAmount, { precision: countDecimalPlaces(unitOrderQty), toFixed: true })}
              </span>
              <span>{stakingCurrency}</span>
            </dd>
          </div>
          <div className={sectionDescriptionItem}>
            <dt>
              <p>Currently hedged</p>
              <p>(All short positions)</p>
            </dt>
            <dd>
              <span className={sectionPrice}>
                {numberFormatter(shortOpenPositions, { precision: countDecimalPlaces(unitOrderQty), toFixed: true })}
              </span>
              <span>{stakingCurrency}</span>
            </dd>
          </div>
        </dl>
        <PositionOrder />
      </Modal.Content>
      <Modal.Footer>
        <FooterButton onClose={onClose} />
      </Modal.Footer>
    </>
  );
};

export default HedgingForm;

const sectionDescriptionItem = flex({
  justifyContent: 'space-between',
  '& + &': {
    mt: 8,
  },
});

const sectionPrice = css({
  textStyle: 'Text/L/Strong',
  color: 'content.default.level-4',
  mr: 4,
});

const PositionOrder = () => {
  const { stakingCurrency } = useContext(HedgingFormContext);
  const { symbolInfoAtom, isHedgeOverStakingAmountAtom, isOverMaxPositionsAtom, marginAtom } =
    useBunja(hedgingFormBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const isHedgeOverStakingAmount = useAtomValue(isHedgeOverStakingAmountAtom);
  const isOverMaxPositions = useAtomValue(isOverMaxPositionsAtom);

  const { getMarginAtom } = useBunja(marginsBunja);
  const margins = useAtomValue(getMarginAtom);
  const marginType = useSymbolMarginType(symbolInfo?.symbol);
  const margin = useAtomValue(marginAtom);

  if (!symbolInfo) return null;

  const availableFunds = getAvailableFunds(marginType, margins, symbolInfo?.quoteCurrency);
  const isAvailableFunds = Num(availableFunds).isLessThan(margin);

  const contents = [
    {
      condition: isHedgeOverStakingAmount,
      description:
        'You have successfully hedged your entire staked amount. To place additional orders, please proceed.',
      isError: false,
    },
    {
      condition: isOverMaxPositions,
      description: `Your ${stakingCurrency} positions are maxed out (${maxPositionLimit}/${maxPositionLimit})`,
      isError: true,
    },
    {
      condition: isAvailableFunds,
      description: 'Available assets are too low to proceed with your hedge. Please deposit funds to continue',
      isError: true,
    },
  ].find(({ condition }) => condition);

  if (!contents) return <PositionOrderCard />;

  return (
    <div className={css({ mt: 16, pt: 16, borderTop: '1px solid', borderColor: 'border.default.level-1' })}>
      <p
        className={css({
          textStyle: 'Text/L/Regular',
          color: contents.isError ? 'content.state.negative' : 'content.default.level-4',
        })}
      >
        {contents.description}
      </p>
    </div>
  );
};

interface FooterButtonProps {
  onClose: () => void;
}

const FooterButton = ({ onClose }: FooterButtonProps) => {
  const { openWalletModal } = useWalletModal();
  const { stakingCurrency } = useContext(HedgingFormContext);
  const { isHedgeOverStakingAmountAtom, isOverMaxPositionsAtom, symbolInfoAtom, marginAtom } =
    useBunja(hedgingFormBunja);
  const { getMarginAtom } = useBunja(marginsBunja);

  const isHedgeOverStakingAmount = useAtomValue(isHedgeOverStakingAmountAtom);
  const isOverMaxPositions = useAtomValue(isOverMaxPositionsAtom);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const marginType = useSymbolMarginType(symbolInfo?.symbol);
  const margins = useAtomValue(getMarginAtom);
  const margin = useAtomValue(marginAtom);

  if (!symbolInfo) return null;

  const availableFunds = getAvailableFunds(marginType, margins, symbolInfo?.quoteCurrency);
  const isAvailableFunds = Num(availableFunds).isLessThan(margin);

  if (isHedgeOverStakingAmount)
    return (
      <SegmentClick
        event={StakeEvent.StakeHedgeSolutionClick}
        data={{ currency: stakingCurrency, button_text: 'Add more orders' }}
      >
        <Button as='a' href='/trade' onClick={onClose} hierarchy='primary' size='large' label='Add more orders' />
      </SegmentClick>
    );
  if (isOverMaxPositions)
    return (
      <SegmentClick
        event={StakeEvent.StakeHedgeSolutionClick}
        data={{ currency: stakingCurrency, button_text: 'Manage positions' }}
      >
        <Button as='a' href='/trade' onClick={onClose} hierarchy='primary' size='large' label='Manage positions' />
      </SegmentClick>
    );
  if (isAvailableFunds)
    return (
      <SegmentClick
        event={StakeEvent.StakeHedgeSolutionClick}
        data={{ currency: stakingCurrency, button_text: 'Add funds' }}
      >
        <Button
          as='button'
          hierarchy='primary'
          onClick={() => {
            onClose();
            openWalletModal('deposit');
          }}
          size='large'
          label='Add funds'
        />
      </SegmentClick>
    );
  return <PositionOrderButton onClose={onClose} />;
};

const getAvailableFunds = (marginType: OrderMarginType, margins: Margins, quoteCurrency: string) => {
  if (!margins) return '0';

  switch (marginType) {
    case 'Cross':
      return margins[quoteCurrency]?.crossMarginAvailable || '0';
    case 'Isolated':
      return margins[quoteCurrency]?.isolatedMarginAvailable || '0';
    default:
      marginType satisfies never;
      // oxlint-disable-next-line no-console
      console.error(`Unexpected marginType: ${marginType}`);
      return '0';
  }
};
